export const GSI_ONE_TAP_CONTAINER_ID = 'credential_picker_container';
export const GSI_ONE_TAP_OFFSET_VAR = '--gsi-one-tap-bottom-offset';

/**
 * How many CSS pixels of the layout viewport are hidden below the visual
 * viewport. On iOS Safari this is the browser toolbar (and similar chrome
 * on other mobile browsers) sitting on top of `position: fixed; bottom: 0`.
 */
export function getVisualViewportBottomOverlap({
  innerHeight,
  visualViewport,
} = {}) {
  if (!visualViewport || typeof visualViewport.height !== 'number') {
    return 0;
  }

  const layoutHeight = Number(innerHeight) || 0;
  const visualHeight = Number(visualViewport.height) || 0;
  const offsetTop = Number(visualViewport.offsetTop) || 0;
  const overlap = layoutHeight - visualHeight - offsetTop;

  return Math.max(0, Math.round(overlap));
}

export function applyGoogleOneTapBottomOffset(
  overlap,
  root = typeof document !== 'undefined' ? document.documentElement : null,
  doc = typeof document !== 'undefined' ? document : null
) {
  const px = `${Math.max(0, Number(overlap) || 0)}px`;

  if (root?.style) {
    root.style.setProperty(GSI_ONE_TAP_OFFSET_VAR, px);
  }

  const container = doc?.getElementById?.(GSI_ONE_TAP_CONTAINER_ID);
  if (container?.style) {
    // Google sets an inline `bottom: 0`. !important keeps the prompt above
    // browser chrome even if GIS rewrites the inline style without it.
    container.style.setProperty(
      'bottom',
      `calc(${px} + env(safe-area-inset-bottom, 0px))`,
      'important'
    );
  }

  return px;
}

export function startGoogleOneTapPositionSync({
  windowObj = typeof window !== 'undefined' ? window : null,
  documentObj = typeof document !== 'undefined' ? document : null,
} = {}) {
  if (!windowObj || !documentObj) {
    return () => {};
  }

  const update = () => {
    const overlap = getVisualViewportBottomOverlap({
      innerHeight: windowObj.innerHeight,
      visualViewport: windowObj.visualViewport,
    });
    applyGoogleOneTapBottomOffset(overlap, documentObj.documentElement, documentObj);
  };

  update();

  const visualViewport = windowObj.visualViewport;
  visualViewport?.addEventListener('resize', update);
  visualViewport?.addEventListener('scroll', update);
  windowObj.addEventListener('resize', update);
  windowObj.addEventListener('orientationchange', update);

  const observer = new MutationObserver(update);
  if (documentObj.body) {
    observer.observe(documentObj.body, { childList: true });
  }

  return () => {
    visualViewport?.removeEventListener('resize', update);
    visualViewport?.removeEventListener('scroll', update);
    windowObj.removeEventListener('resize', update);
    windowObj.removeEventListener('orientationchange', update);
    observer.disconnect();
  };
}
